// The Money message catalog (Slice 6; docs/phase-0/slice6-ux-flows.md §3,
// docs/phase-0/slice6-component-designs.md §3). No copywriter draft file exists yet for
// Slice 6 (verified: docs/copy/ contains only slice4-certify-copy.md) - this mirrors
// apps/web/src/copy/certify.ts and apps/web/src/copy/build.ts's own established
// precedent exactly: every string the ux/component-design specs quote or shell as
// `[copy: flagged for review]` is reproduced here and rendered as DRAFT, never
// finalized by this build lane (CLAUDE.md §8 - legal/domain-sensitive copy is the
// copywriter's + counsel's lane, not this one's); every other key is a plain
// structural label (a button name, a heading, a field label), not a new financial
// claim, so it carries no separate flag, per certify.ts's own bare-label convention.
//
// SCOPED, ENGLISH-ONLY, LOCAL TO apps/web - the same gap certify.ts/build.ts name: no
// i18n-routed ICU MessageFormat mechanism exists in this repo yet (the
// i18n-engineer's lane, `i18n-localization`). `moneyCopy()` below does plain
// `{token}` substitution only, no ICU plural rules.
//
// D27/D48 IS LOAD-BEARING HERE (docs/phase-0/decisions-adr.md:65,109; ADR-0023): no key
// below ever carries a per-fix, per-action, or per-token placeholder, and no key ever
// states a figure inline (every dollar/count value is always an interpolated `{var}`
// the caller supplies from a real, sourced field on the landed `MoneyView`/
// `BillingInboxItem` schemas - packages/shared/src/model/money-view.ts - never a
// literal number baked into the copy itself).
//
// D98 GOVERNS THE PLAN-CAPACITY BLOCK. `crew/config/plan-language.json` is the only
// sanctioned source for customer-facing capacity vocabulary; it carries both the approved
// strings (`strings.*`) and a forbidden list, and `scripts/checks/111-customer-copy-
// vocabulary.sh` enforces it mechanically. The plan-capacity keys below are that file's
// own `strings.*` values, verbatim except that each currency figure is moved into an
// interpolated `{var}` so the caller supplies it already formatted (the paragraph above).
// Three rules this catalog broke until now, each fixed here:
//   - The countable thing a founder sees is a CHANGE. Never a "fix", never a "unit"
//     (`the_countable_thing`, and `never: "{n} fixes this month"`, which is exactly what
//     `usedOfIncluded` said).
//   - The word "overage" never reaches a customer surface. It is an invoicing word, so the
//     line states the per-change PRICE from `PlanCapacity.overagePriceMinor` instead
//     (ux spec §3.4: "the honest version is the price"). "billed at" went with it - a
//     neighbour of the forbidden "billed at cost".
//   - The founder's monthly spend guardrail is a LIMIT, never a "ceiling". Two different
//     things were both called a ceiling: D48's limit, which the founder sets and sees, and
//     D98's internal cost ceiling, which they must never see. One word for both is how the
//     internal one leaks (plan-language.json:230). The wire field keeps its schema name
//     (`monthlyCeilingMinor`/`ceilingSource`); only what a founder reads changes.
// This is a vocabulary-contract fix. It does not make Slice 6 copy final - the DRAFT
// posture above still stands and the copywriter's + counsel's pass is still owed.
export const MONEY_COPY = {
  "money.tab.title": "Money",

  // ---- All-in cost (ux spec §3.2; component-designs §3.2) ----
  "money.allInCost.heading": "All-in cost",
  "money.allInCost.subtitle": "This month, in {currency}.",
  "money.allInCost.category.cloud": "Cloud",
  "money.allInCost.category.services": "Services",
  "money.allInCost.category.nightcrew_plan": "Nightcrew plan",
  "money.allInCost.total": "Total",
  // ADR-0023 decision 7: the flat plan price is a known list price, never "modeled
  // estimate" (that label is reserved for a Nightcrew-derived rollup/projection).
  "money.allInCost.nightcrewPlanNote": "Included in your plan",
  "money.allInCost.emptyCategory": "Nothing logged yet",
  "money.allInCost.addCostLineCta": "Add a cost line",
  "money.allInCost.sourcedFromLabel": "Sourced from",
  "money.allInCost.source.connector": "Live connector",
  "money.allInCost.source.upload": "Upload",
  "money.allInCost.source.manual_entry": "Manual entry",
  "money.allInCost.source.forwarded_email": "Forwarded email",

  // ---- The P&L (ux spec §3.3; component-designs §3.3) - abstain/partial/computed ----
  "money.pnl.heading": "The P&L",
  "money.pnl.insufficientData.chip": "Not enough data yet",
  "money.pnl.partial.revenueKnown": "Revenue: {amount}",
  "money.pnl.partial.costKnown": "Cost: {amount}",
  "money.pnl.partial.revenueMissing": "Revenue",
  "money.pnl.partial.costMissing": "Cost",
  "money.pnl.partial.notLoggedYet": "Not logged yet",
  // ASCII hyphen, matching the spec's own text (ux spec §3.3: "Revenue {X} - cost {Y} =
  // {Z} this month"). It carried U+2212 MINUS SIGN until now, and that one glyph is why
  // check 111 could not read this file at all on a Windows shell: the extractor printed it
  // through a cp1252 stdout, raised UnicodeEncodeError, and the check skipped the file and
  // still reported PASS. The check no longer skips silently (its own fix), and the copy no
  // longer needs a non-ASCII character to say "minus".
  "money.pnl.computed.line": "Revenue {revenue} - cost {cost} = {net} this month",
  "money.pnl.computed.profit": "Profit",
  "money.pnl.computed.loss": "Loss",
  "money.pnl.computed.seeCostBreakdownCta": "See the cost breakdown",

  // ---- Plan capacity (ux spec §3.4, which that section marks REWRITTEN under D98;
  //      component-designs §3.4 for the shape; crew/config/plan-language.json for every
  //      string) ----
  "money.planCapacity.heading": "Plan capacity",
  // The gauge caption has three states in the sanctioned source (`gauge_healthy`,
  // `gauge_near`, `gauge_over`) and the panel now renders ALL THREE. `gauge_near` was
  // withheld until ADR-0038 because its "nearly there" threshold was undecided and
  // inventing one would have been a claim about the customer's month; the ADR decided it
  // at `ceil(0.8 * included)` with a time gate, and `selectGaugeState` in
  // @nightcrew/money owns the rule. `gaugeOver` is the whole caption in the over state,
  // not a line appended below `gaugeHealthy` - it restates the shipped count itself, so
  // rendering both would say it twice.
  "money.planCapacity.gaugeHealthy": "Your crew has shipped {used} of about {included} changes this month.",
  // `gauge_near` is `gauge_healthy` PLUS FOUR WORDS, and those four words are the only
  // reason this state needed a decision: "Plenty of time left" is a claim about the
  // CALENDAR, not about the count, and section 0 rule 4 forbids asserting one the system
  // cannot back. The gate that backs it lives in `selectGaugeState`; this file only holds
  // the sanctioned string, byte for byte, as check 111 requires.
  "money.planCapacity.gaugeNear":
    "Your crew has shipped {used} of about {included} changes this month. Plenty of time left.",
  "money.planCapacity.gaugeOver":
    "Your crew has shipped {used} changes this month, {extra} more than your plan includes. Extra changes are {rate} each, and we always show you the size before we start.",
  // `strings.gauge_over_capped` (ADR-0031). The capped month cannot use `gaugeOver`: that
  // sentence uses one number for two jobs, and once D98's upgrade cap binds the raw excess
  // and the billed count diverge, so a per-change rate beside either one multiplies out to a
  // figure the founder will not be sent. This states the month TOTAL instead, which removes
  // the multiplication rather than picking a side. `{extra}` keeps its only true meaning
  // (work past the allowance) and so still agrees with `extraChangesChip`.
  "money.planCapacity.gaugeOverCapped":
    "Your crew has shipped {used} changes this month, {extra} more than your plan includes. We have capped this month at {amount}, so you never pay more than the next plan up.",
  "money.planCapacity.extraChangesCappedNote":
    "Past a point, extra changes stop adding to the bill. You are on the cheaper of the two prices, automatically.",
  // component-designs §3.4's badge, with "overage" out of it. Calm by construction: a
  // `.status-chip.neutral`, never `.meter.over`'s red fill (that spec's own emphatic note).
  "money.planCapacity.extraChangesChip": "+{extra} more this month",
  // `strings.rework_free` (D99). The only place a founder sees what the platform did not
  // charge for, shown as a promise with a count beside it rather than buried in an FAQ.
  "money.planCapacity.reworkFree": "If we get a change wrong, fixing it is on us. It does not come out of your month.",
  "money.planCapacity.limitLabel": "Monthly limit: {amount}",
  // `strings.ceiling_line`, verbatim except for the inline `$` the caller's formatter emits (the
  // same one deliberate difference `gaugeOver` carries, and for the same reason: keeping both would
  // print `$$500.00`).
  //
  // IT HAS NO RENDERER AGAIN, AND THAT IS THE FIX RATHER THAN A REGRESSION. An earlier version of
  // this comment said the promise "was enforced by the shift engine and never actually said to
  // anyone". The second half was true and the first was not. `shift/ceiling-resolver.ts:98-107`
  // compares the app's limit against `sum(run_ledger.cost_micro_eur) where account_id = $1`, and
  // `run_ledger` has no `app_id` column (measured: 11 columns, none of them an app), so what the
  // sentence calls "it" is neither this app's spend nor the founder's bill - it is the ACCOUNT's
  // internal model cost. A panel drove two apps on one Studio account and app B paused on app A's
  // spend with none of its own limit used. So the panel now renders `limitSource.founder` in this
  // slot and the promise is withheld until CR-039 is ruled: either the enforcement bounds what the
  // sentence names, or the sentence changes. The register keeps the owner's wording byte for byte
  // (`crew/config/plan-language.json:_ceiling_line_is_not_rendered`); this catalog keeps the key so
  // the pin against the register still holds.
  //
  // THE SLOT IS `{amount}`, NOT `{ceiling}`, and the rename is not cosmetic. The bare word `ceiling`
  // is on `internal_vocabulary_never_shown`, and this catalog is scanned for it - a slot named
  // `{ceiling}` turned that test red the moment this string landed. A slot NAME is never rendered, so
  // renaming it changes nothing a founder reads while keeping the catalog clean of the internal word.
  // The same one deliberate difference as `gaugeOver`: the register writes the currency symbol inline
  // and this catalog never does, because the caller supplies an already-formatted amount.
  "money.planCapacity.limitPromise":
    "You have set a monthly limit of {amount}. We never go past it without asking.",
  // `strings.extra_changes_line`, same `$` rule. Also had zero renderers. It is the calm statement of
  // the extra-change price BEFORE a month goes over, distinct from `gaugeOver`, which states it after
  // the fact with a count attached. Rendering only the second one meant the price was a surprise the
  // first time it appeared, which is the thing this slice's goal sentence rules out.
  "money.planCapacity.extraChangesLine":
    "Busy month? Extra changes are {rate} each. Nothing runs before you approve it.",
  // NAMES A DISCOUNT THAT WAS OTHERWISE SILENT. A verification panel measured a founding partner's
  // panel rendering $10.50 an extra change while `plan-language.json` publishes $12.50 for the same
  // tier, with nothing on the screen connecting the two. The founder is not being overcharged - the
  // opposite - but a price that contradicts the published one and explains nothing is the same class
  // of problem, and check 111 cannot catch it because it enforces vocabulary rather than agreement
  // with the register.
  //
  // IT STATES NO TERMS, and the second sentence it used to carry is why that rule is written down.
  // It read "These prices stay yours until we close the founding programme", and `founding-rate.ts`
  // stores the RULE rather than the price on purpose, so what stays the founder's is the 30%
  // DISCOUNT and the price does not: a panel moved `plans.price` 9900 -> 14900 with the programme
  // open and the founder's billed figure moved 6930 -> 10430. D57 sold price certainty, so the
  // contradiction is a real decision (promise the discount, or store a locked price and pay 0029's
  // second-source-of-truth cost) and the wording is section 8 owner-approved. CR-039 carries it. The
  // true half ships: the rate is NAMED, which is what AC2 asks for.
  "money.planCapacity.foundingRateApplied": "Founding partner rate applied.",
  // RENDERED NOW, and the reason it must be is a defect a panel caught. `limitPromise` credits the
  // FOUNDER ("you have set a monthly limit of..."), and AC3 clause 1 auto-sets one on every new app -
  // so on an untouched app the panel credited the founder with a figure the system chose. This is
  // what renders instead, and it had zero renderers until then.
  "money.planCapacity.limitSource.auto": "Auto-set for your plan",
  "money.planCapacity.limitSource.founder": "Set by you",
  "money.planCapacity.noLimit": "No monthly limit set yet",
  "money.planCapacity.changeCta": "Change",
  "money.planCapacity.viewerDeniedReason": "Only an owner or admin can change the monthly limit.",
  // AC3 CLAUSE 3, the keep-or-change prompt. Five strings, all sanctioned in
  // `crew/config/plan-language.json` before a line of this was written - the register is the source
  // and this catalog reproduces it, which is the same rule every other string here follows.
  //
  // `limitPromptKeptAck` MAKES A PROMISE ("We will not ask again for this app") and the promise is
  // kept by a stored column, `apps.limit_prompt_answered_at`, not by a session variable. A promise
  // enforced by memory that a page reload clears would be a lie the second time the founder logged in.
  "money.planCapacity.limitPrompt.heading": "Keep your monthly limit where it is?",
  // WHAT THIS SENTENCE MAY CLAIM IS BOUNDED BY WHAT TRIGGERS IT (section 0 rule 4). It used to open
  // "Your crew is on track to reach your {amount} monthly limit this month", and a verification
  // panel refuted that by execution: the trigger is `selectGaugeState(...) === "near"`, whose entire
  // input is changes used against changes included plus a calendar fraction. It never reads the
  // ceiling. The panel drove an app to a $21,474,836.47 limit against a $49.99 bill and the system
  // still asserted it was "on track to reach" it, purely because 80 of 100 changes were used.
  //
  // So the sentence states only the two facts the gate actually measures - most of the included
  // changes are gone, and the month is not over - plus the charge for going past the plan and the
  // choice the prompt exists to offer. The causal chain is left for the reader to draw rather than
  // asserted as a measured trajectory.
  //
  // AND THE REPLACEMENT CLAUSE WAS REFUTED IN ITS TURN, one clause over from the one it fixed. It
  // read "your monthly limit of {amount} is where work pauses until you say otherwise", and the
  // comment above it named `shift/ceiling-resolver.ts` as enforcing that - a file this catalog's
  // author had not executed. What it does (`ceiling-resolver.ts:98-107`) is compare the PER-APP
  // limit against `sum(cost_micro_eur) from run_ledger where account_id = $1`: the ACCOUNT's
  // internal model cost, because `run_ledger` has no `app_id` column. A panel put two apps on one
  // Studio account (`app_limit` 3) and app B paused on app A's spend with none of its own limit
  // used, and read the paused app's own panel showing 44 of 100 changes used and nothing billed past
  // the plan. So the amount this sentence names is not what pauses this app and is not the bill
  // either. The clause is gone; the limit is still NAMED, because keeping or raising it is what the
  // prompt asks. CR-039 carries the choice between fixing the enforcement and rewording the promise.
  "money.planCapacity.limitPrompt.body":
    "Your crew has used most of the changes included in your plan, with time left this month. Anything past your plan is charged. You can leave your monthly limit of {amount} as it is or raise it now.",
  "money.planCapacity.limitPrompt.keepCta": "Keep it",
  "money.planCapacity.limitPrompt.changeCta": "Raise it",
  "money.planCapacity.limitPrompt.keptAck": "Left at {amount}. We will not ask again for this app.",
  "money.planCapacity.limitDialog.heading": "Change your monthly limit?",
  "money.planCapacity.limitDialog.fieldLabel": "New monthly limit",
  "money.planCapacity.limitDialog.currencySuffix": "USD",
  "money.planCapacity.limitDialog.saveCta": "Save",
  "money.planCapacity.limitDialog.savingCta": "Saving...",
  "money.planCapacity.limitDialog.cancelCta": "Cancel",
  "money.planCapacity.limitDialog.errorBody": "That didn't save. Try again.",
  "money.planCapacity.limitDialog.savedToast": "Monthly limit updated.",

  // ---- The billing inbox (ux spec §3.5; component-designs §3.5) - a cost ledger only ----
  "money.billingInbox.heading": "Billing inbox",
  "money.billingInbox.empty": "No entries logged yet. Entries you add appear here.",
  "money.billingInbox.addEntryCta": "Add an entry",
  "money.billingInbox.viewerDeniedReason": "Only an owner or admin can add a billing entry.",
  "money.billingInbox.entryForm.heading": "Add a billing-inbox entry",
  "money.billingInbox.entryForm.kindLabel": "What is this?",
  "money.billingInbox.entryForm.kind.cloud": "Cloud",
  "money.billingInbox.entryForm.kind.services": "Services",
  "money.billingInbox.entryForm.amountLabel": "Amount",
  "money.billingInbox.entryForm.currencySuffix": "USD",
  "money.billingInbox.entryForm.periodLabel": "Period",
  "money.billingInbox.entryForm.periodPlaceholder": "2026-07",
  "money.billingInbox.entryForm.sourceLabel": "How did this reach you?",
  "money.billingInbox.entryForm.source.manual_entry": "Manual entry",
  "money.billingInbox.entryForm.source.forwarded_email": "Forwarded email",
  "money.billingInbox.entryForm.sourceRefLabel": "Reference (optional)",
  "money.billingInbox.entryForm.saveCta": "Save",
  "money.billingInbox.entryForm.savingCta": "Adding...",
  "money.billingInbox.entryForm.cancelCta": "Cancel",
  "money.billingInbox.entryForm.errorBody": "That didn't save. Try again.",
  "money.billingInbox.row.periodAdded": "{period} · added {relative}",
  "money.billingInbox.row.ref": "Ref: {ref}",

  // ---- Loading / error (screen-local, ux spec §3.6) ----
  "money.error.title": "We couldn't load your money view.",
  "money.error.retryCta": "Try again",
} as const;

export type MoneyCopyKey = keyof typeof MONEY_COPY;

/**
 * Plain `{token}` substitution only - no ICU plural rules (matching `certifyCopy`/
 * `buildCopy`'s own established precedent exactly). A missing var leaves the literal
 * `{token}` in place rather than throwing, so a caller mistake is visible in the
 * render instead of a crash.
 */
export const moneyCopy = (key: MoneyCopyKey, vars?: Record<string, string | number>): string => {
  const template = MONEY_COPY[key];
  if (vars === undefined) return template;
  return template.replace(/\{(\w+)\}/g, (match: string, name: string) => {
    const value = vars[name];
    return value === undefined ? match : String(value);
  });
};
